import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { drName } from "@/lib/utils";
import Seo from "@/components/Seo";
import {
  Stethoscope, MapPin, ArrowRight, ShieldCheck, Zap, Calendar,
  Smartphone, Gift, CheckCircle2, Users,
} from "lucide-react";

const slug = s => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
const unslug = s => (s || "").split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

/** Fetch the list of cities that currently have at least one real (non-demo,
 * non-test, approved & active) doctor. Used to flip city pages between
 * indexable and noindex without a rebuild. */
function useIndexedCities() {
  const [indexed, setIndexed] = useState(null);
  useEffect(() => {
    api.get("/seo/indexed-cities")
      .then(r => setIndexed(new Set((r.data?.cities || []).map(c => slug(c)))))
      .catch(() => setIndexed(new Set()));
  }, []);
  return indexed;
}

function useAllCities() {
  const [cities, setCities] = useState([]);
  useEffect(() => {
    api.get("/cities").then(r => setCities(r.data?.cities || [])).catch(() => {});
  }, []);
  return cities;
}

// ---------- i18n content for ParaMedicosLanding ----------
const PM_CONTENT = {
  es: {
    seoTitle: "Software para consultorio médico en Ecuador",
    seoDesc: "Agendik es el software de agenda en línea para médicos en Ecuador. Recordatorios automáticos, aplicación móvil, perfil verificado. 30 días de prueba gratuita, $49/mes. Con nuestro programa Socio referidor obtiene $7 de descuento al mes por cada colega referido, hasta gratis.",
    heroTitle: "Software para consultorio médico en Ecuador",
    heroPara: <>Agendik es la agenda en línea diseñada para médicos ecuatorianos. Sus pacientes reservan solos 24/7, reciben recordatorios automáticos, y usted gestiona todo desde su teléfono. <strong>$49/mes</strong>, con <strong>30 días de prueba gratuita</strong>.</>,
    ctaStart: "Empezar 30 días gratis",
    ctaSearch: "Ver médicos Agendik",
    videoCap: "Agendik para médicos en 20 segundos",
    benefitsTitle: "¿Por qué Agendik?",
    benefits: [
      { title: "Agenda 24/7", body: "Sus pacientes reservan solos, desde su teléfono, incluso fuera de horario." },
      { title: "Recordatorios automáticos", body: "Menos ausencias gracias a los recordatorios por email y WhatsApp." },
      { title: "Perfil verificado", body: "Los nuevos pacientes le encuentran por especialidad y ciudad." },
      { title: "Aplicación móvil", body: "Todo desde su teléfono: agenda, pacientes, notificaciones." },
      { title: "Historial simple", body: "Notas privadas por paciente, sin complicaciones ni exportaciones." },
      { title: "Cumplimiento LOPDP", body: "Datos alojados con cifrado. Cumplimos la ley ecuatoriana de datos personales." },
    ],
    howTitle: "Cómo funciona",
    how: [
      { t: "Cree su cuenta", d: "Registro en 3 minutos. Suba su título y su cédula profesional; validamos su perfil." },
      { t: "Configure su agenda", d: "Elija sus horarios, duración de consulta, tarifa, métodos de pago y seguros aceptados." },
      { t: "Reciba pacientes", d: "Su perfil verificado aparece en Agendik. Los pacientes reservan solos y usted recibe alertas." },
    ],
    priceTitle: "Precio simple",
    perMonth: "/mes",
    priceLead: <><strong>30 días gratis, sin tarjeta.</strong> Al final de la prueba, agregue su método de pago para continuar: $49/mes.</>,
    priceBullets: [
      "Todas las funciones incluidas",
      "Pacientes ilimitados",
      "Recordatorios automáticos ilimitados",
      "Soporte por email y WhatsApp",
    ],
    refTitle: "Programa Socio referidor",
    refPara: <>Recomiende Agendik a sus colegas: <strong>$7 de descuento al mes por cada colega referido, hasta gratis</strong>. Los descuentos son acumulables y se aplican mientras su colega siga suscrito.</>,
    refSocioLead: "También puede unirse como Socio referidor externo (no médico) y cobrar comisiones.",
    refSocioLink: "Descubrir el programa",
    faqTitle: "Preguntas frecuentes",
    faq: [
      { q: "¿Puedo cancelar cuando quiera?", a: "Sí, sin permanencia. Cancele en un clic desde su panel; conserva el acceso hasta el final del periodo pagado." },
      { q: "¿Los 30 días de prueba son realmente gratuitos?", a: "Sí. No pedimos tarjeta al registrarse. No cobramos nada durante los 30 días. Al final de la prueba, agregue su método de pago para continuar; sin método de pago, su perfil se pausa y no cobramos nada." },
      { q: "¿Cómo funciona el programa Socio referidor?", a: "Comparta su código de referido con colegas médicos. Por cada colega que se suscribe, obtiene $7/mes de descuento acumulable en su factura, hasta gratis." },
      { q: "¿Mis datos y los de mis pacientes están seguros?", a: "Sí. Los datos personales de pacientes y los datos bancarios de socios referidores se cifran en base de datos. Cumplimos la ley ecuatoriana LOPDP." },
      { q: "¿Puedo usar Agendik desde el teléfono?", a: "Sí. La aplicación se instala en Android y iPhone. Su agenda, sus pacientes y sus notificaciones están siempre a mano." },
    ],
    citiesTitle: "Agendik por ciudad",
    citiesLead: "Somos usados por médicos en todo Ecuador. Descubra cómo Agendik les ayuda en su ciudad :",
    cityLink: c => `Software médico en ${c}`,
  },
  fr: {
    seoTitle: "Logiciel pour cabinet médical en Équateur",
    seoDesc: "Agendik est l'agenda en ligne pour les médecins en Équateur. Rappels automatiques, application mobile, profil vérifié. 30 jours d'essai gratuit, $49/mois. Avec notre programme Socio referidor, $7 de réduction mensuelle par confrère parrainé, jusqu'à la gratuité.",
    heroTitle: "Logiciel pour cabinet médical en Équateur",
    heroPara: <>Agendik est l'agenda en ligne conçu pour les médecins équatoriens. Vos patients réservent seuls 24h/24, reçoivent des rappels automatiques, et vous gérez tout depuis votre téléphone. <strong>$49/mois</strong>, avec <strong>30 jours d'essai gratuit</strong>.</>,
    ctaStart: "Commencer 30 jours gratuits",
    ctaSearch: "Voir les médecins Agendik",
    videoCap: "Agendik pour les médecins en 20 secondes",
    benefitsTitle: "Pourquoi Agendik ?",
    benefits: [
      { title: "Agenda 24h/24", body: "Vos patients réservent seuls, depuis leur téléphone, même en dehors des horaires." },
      { title: "Rappels automatiques", body: "Moins d'absences grâce aux rappels par email et WhatsApp." },
      { title: "Profil vérifié", body: "Les nouveaux patients vous trouvent par spécialité et par ville." },
      { title: "Application mobile", body: "Tout depuis votre téléphone : agenda, patients, notifications." },
      { title: "Historique simple", body: "Notes privées par patient, sans complications ni exports." },
      { title: "Conformité LOPDP", body: "Données hébergées avec chiffrement. Nous respectons la loi équatorienne sur les données personnelles." },
    ],
    howTitle: "Comment ça marche",
    how: [
      { t: "Créez votre compte", d: "Inscription en 3 minutes. Téléchargez votre diplôme et votre carte professionnelle ; nous validons votre profil." },
      { t: "Configurez votre agenda", d: "Choisissez vos horaires, la durée de consultation, le tarif, les moyens de paiement et les assurances acceptées." },
      { t: "Recevez des patients", d: "Votre profil vérifié apparaît sur Agendik. Les patients réservent seuls et vous recevez des alertes." },
    ],
    priceTitle: "Tarif simple",
    perMonth: "/mois",
    priceLead: <><strong>30 jours gratuits, sans carte.</strong> À la fin de l'essai, ajoutez votre moyen de paiement pour continuer : $49/mois.</>,
    priceBullets: [
      "Toutes les fonctionnalités incluses",
      "Patients illimités",
      "Rappels automatiques illimités",
      "Support par email et WhatsApp",
    ],
    refTitle: "Programme Socio referidor",
    refPara: <>Recommandez Agendik à vos confrères : <strong>$7 de réduction mensuelle par confrère parrainé, jusqu'à la gratuité</strong>. Les réductions sont cumulables et s'appliquent tant que votre confrère reste abonné.</>,
    refSocioLead: "Vous pouvez aussi rejoindre le programme comme Socio referidor externe (non médecin) et toucher des commissions.",
    refSocioLink: "Découvrir le programme",
    faqTitle: "Questions fréquentes",
    faq: [
      { q: "Puis-je annuler à tout moment ?", a: "Oui, sans engagement. Annulez en un clic depuis votre tableau de bord ; vous conservez l'accès jusqu'à la fin de la période payée." },
      { q: "Les 30 jours d'essai sont-ils vraiment gratuits ?", a: "Oui. Nous ne demandons aucune carte à l'inscription. Nous ne débitons rien pendant les 30 jours. À la fin de l'essai, ajoutez votre moyen de paiement pour continuer ; sans moyen de paiement, votre profil est mis en pause et rien n'est prélevé." },
      { q: "Comment fonctionne le programme Socio referidor ?", a: "Partagez votre code de parrainage avec des confrères médecins. Pour chaque confrère qui s'abonne, vous recevez $7/mois de réduction cumulable sur votre facture, jusqu'à la gratuité." },
      { q: "Mes données et celles de mes patients sont-elles sécurisées ?", a: "Oui. Les données personnelles des patients et les coordonnées bancaires des Socios referidores sont chiffrées en base de données. Nous respectons la loi équatorienne LOPDP." },
      { q: "Puis-je utiliser Agendik depuis mon téléphone ?", a: "Oui. L'application s'installe sur Android et iPhone. Votre agenda, vos patients et vos notifications sont toujours à portée de main." },
    ],
    citiesTitle: "Agendik par ville",
    citiesLead: "Des médecins l'utilisent partout en Équateur. Découvrez comment Agendik les aide dans votre ville :",
    cityLink: c => `Logiciel médical à ${c}`,
  },
  en: {
    seoTitle: "Medical practice software in Ecuador",
    seoDesc: "Agendik is the online scheduling software for doctors in Ecuador. Automatic reminders, mobile app, verified profile. 30-day free trial, $49/month. With our Socio referidor program, get $7 off per month for each colleague referred, up to free.",
    heroTitle: "Medical practice software in Ecuador",
    heroPara: <>Agendik is the online scheduling tool designed for Ecuadorian doctors. Your patients book on their own 24/7, get automatic reminders, and you manage everything from your phone. <strong>$49/month</strong>, with a <strong>30-day free trial</strong>.</>,
    ctaStart: "Start 30 days free",
    ctaSearch: "See Agendik doctors",
    videoCap: "Agendik for doctors in 20 seconds",
    benefitsTitle: "Why Agendik?",
    benefits: [
      { title: "24/7 schedule", body: "Your patients book on their own, from their phone, even outside office hours." },
      { title: "Automatic reminders", body: "Fewer no-shows thanks to email and WhatsApp reminders." },
      { title: "Verified profile", body: "New patients find you by specialty and city." },
      { title: "Mobile app", body: "Everything from your phone: schedule, patients, notifications." },
      { title: "Simple records", body: "Private notes per patient, no complications, no exports." },
      { title: "LOPDP compliance", body: "Encrypted data hosting. We comply with Ecuador's personal data law." },
    ],
    howTitle: "How it works",
    how: [
      { t: "Create your account", d: "Sign up in 3 minutes. Upload your diploma and professional ID; we validate your profile." },
      { t: "Set up your schedule", d: "Choose your hours, consultation length, fee, payment methods and accepted insurances." },
      { t: "Receive patients", d: "Your verified profile appears on Agendik. Patients book on their own and you get alerts." },
    ],
    priceTitle: "Simple pricing",
    perMonth: "/month",
    priceLead: <><strong>30 days free, no card required.</strong> At the end of the trial, add your payment method to continue: $49/month.</>,
    priceBullets: [
      "All features included",
      "Unlimited patients",
      "Unlimited automatic reminders",
      "Email and WhatsApp support",
    ],
    refTitle: "Socio referidor program",
    refPara: <>Recommend Agendik to your colleagues: <strong>$7 off per month for each colleague referred, up to free</strong>. Discounts stack and apply for as long as your colleague remains subscribed.</>,
    refSocioLead: "You can also join as an external Socio referidor (non-doctor) and earn commissions.",
    refSocioLink: "Discover the program",
    faqTitle: "Frequently asked questions",
    faq: [
      { q: "Can I cancel anytime?", a: "Yes, no lock-in. Cancel in one click from your dashboard; you keep access until the end of the paid period." },
      { q: "Is the 30-day trial really free?", a: "Yes. No card is required at signup. We don't charge anything during the 30 days. At the end of the trial, add your payment method to continue; without a payment method, your profile is paused and nothing is charged." },
      { q: "How does the Socio referidor program work?", a: "Share your referral code with doctor colleagues. For each colleague who subscribes, you get $7/month off, stackable, on your invoice, up to free." },
      { q: "Are my data and my patients' data safe?", a: "Yes. Patient personal data and Socio referidor banking details are encrypted at the database level. We comply with Ecuador's LOPDP law." },
      { q: "Can I use Agendik from my phone?", a: "Yes. The app installs on Android and iPhone. Your schedule, patients and notifications are always within reach." },
    ],
    citiesTitle: "Agendik by city",
    citiesLead: "Doctors use it all across Ecuador. See how Agendik helps them in your city:",
    cityLink: c => `Medical software in ${c}`,
  },
};

/** Hero of /para-medicos — pixel-precise layout per spec.
 *  ≥600px: 2-column grid; video (9:16 vertical) matches the exact height of
 *  the "title + paragraph" block via a ResizeObserver (min 240, max 560).
 *  <600px: single column, video below buttons, max 320px wide.
 */
function PmHero({ content, onPrimary, onSearch }) {
  const titleParaRef = useRef(null);
  const [videoH, setVideoH] = useState(null); // null = mobile / not measured
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 600 : true
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isDesktop || !titleParaRef.current) { setVideoH(null); return; }
    const el = titleParaRef.current;
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setVideoH(Math.max(240, Math.min(560, h)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isDesktop, content]); // re-measure on language change

  // 9:16 vertical video: width = height * 9/16
  const videoWidth = videoH ? Math.round(videoH * 9 / 16) : null;

  return (
    <div
      className="mb-14"
      style={isDesktop
        ? { display: "grid", gridTemplateColumns: "minmax(340px, 1fr) auto",
            gridTemplateRows: "auto auto", columnGap: 32, rowGap: 16, alignItems: "start" }
        : { display: "flex", flexDirection: "column", gap: 24 }}
    >
      {/* Left col row 1: title + paragraph (measured) */}
      <div ref={titleParaRef} style={isDesktop ? { gridColumn: 1, gridRow: 1 } : undefined}>
        <h1 className="font-extrabold text-slate-900 mb-4 tracking-tight"
            style={{ fontSize: "clamp(32px, 5vw, 64px)", lineHeight: 1.05 }}>
          {content.heroTitle}
        </h1>
        <p className="text-lg text-slate-600 max-w-3xl">
          {content.heroPara}
        </p>
      </div>

      {/* Right col row 1 (desktop): video spanning both rows height-wise */}
      {isDesktop && (
        <div style={{ gridColumn: 2, gridRow: 1 }}>
          <video
            data-testid="pm-hero-video"
            src="/videos/agendik-pub-video-4x5-20s.mp4#t=0.1"
            controls playsInline preload="metadata"
            style={{
              height: videoH || 480,
              width: videoWidth || 270,
              objectFit: "contain",
              display: "block",
              background: "#000",
            }}
            className="rounded-3xl shadow-md"
          />
          <p className="text-sm text-slate-500 mt-3 text-center" style={{ maxWidth: videoWidth || 270 }}>
            {content.videoCap}
          </p>
        </div>
      )}

      {/* Left col row 2: buttons */}
      <div
        className="flex flex-wrap gap-3"
        style={isDesktop ? { gridColumn: 1, gridRow: 2 } : undefined}
      >
        <Button data-testid="pm-cta-primary" onClick={onPrimary} className="bg-blue-600 hover:bg-blue-700 h-11 px-6">
          {content.ctaStart}
        </Button>
        <Button data-testid="pm-cta-search" variant="outline" onClick={onSearch} className="h-11 px-6">
          {content.ctaSearch}
        </Button>
      </div>

      {/* Mobile only: video below buttons, centered, max 320px */}
      {!isDesktop && (
        <div className="flex flex-col items-center">
          <video
            data-testid="pm-hero-video"
            src="/videos/agendik-pub-video-4x5-20s.mp4#t=0.1"
            controls playsInline preload="metadata"
            style={{ width: "100%", maxWidth: 320, height: "auto", objectFit: "contain", background: "#000" }}
            className="rounded-3xl shadow-md"
          />
          <p className="text-sm text-slate-500 mt-3 text-center">{content.videoCap}</p>
        </div>
      )}
    </div>
  );
}

/** Route: /para-medicos — full marketing landing for doctors. */
export function ParaMedicosLanding() {
  const nav = useNavigate();
  const cities = useAllCities();
  const { lang } = useApp();
  const c = PM_CONTENT[lang] || PM_CONTENT.es;

  const benefitIcons = [Calendar, Zap, ShieldCheck, Smartphone, Users, CheckCircle2];

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12">
      <Seo title={c.seoTitle} description={c.seoDesc}/>

      <PmHero
        content={c}
        onPrimary={() => nav("/doctor/registro")}
        onSearch={() => nav("/buscar")}
      />

      {/* BENEFITS */}
      <section aria-labelledby="pm-benefits" className="mb-16">
        <h2 id="pm-benefits" className="text-2xl font-bold text-slate-900 mb-6">{c.benefitsTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {c.benefits.map((b, i) => {
            const Icon = benefitIcons[i] || CheckCircle2;
            return (
              <Card key={b.title}><CardContent className="p-5">
                <Icon size={22} className="text-blue-600 mb-2"/>
                <h3 className="font-semibold text-slate-900 mb-1">{b.title}</h3>
                <p className="text-sm text-slate-600">{b.body}</p>
              </CardContent></Card>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section aria-labelledby="pm-how" className="mb-16">
        <h2 id="pm-how" className="text-2xl font-bold text-slate-900 mb-6">{c.howTitle}</h2>
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {c.how.map((s, i) => (
            <li key={s.t} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center mb-3">{i + 1}</div>
              <h3 className="font-semibold text-slate-900 mb-1">{s.t}</h3>
              <p className="text-sm text-slate-600">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* PRICING */}
      <section aria-labelledby="pm-pricing" className="mb-16">
        <h2 id="pm-pricing" className="text-2xl font-bold text-slate-900 mb-6">{c.priceTitle}</h2>
        <div className="rounded-2xl border-2 border-blue-600 bg-white p-6 max-w-xl">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-4xl font-extrabold text-slate-900">$49</span>
            <span className="text-slate-500">{c.perMonth}</span>
          </div>
          <p className="text-slate-700 mb-4">{c.priceLead}</p>
          <ul className="text-sm text-slate-600 space-y-1 mb-5">
            {c.priceBullets.map(b => (
              <li key={b} className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 mt-0.5 shrink-0"/>{b}</li>
            ))}
          </ul>
          <Button data-testid="pm-cta-pricing" onClick={() => nav("/doctor/registro")} className="bg-blue-600 hover:bg-blue-700 h-11 px-6">
            {c.ctaStart}
          </Button>
        </div>
      </section>

      {/* REFERRAL / SOCIO REFERIDOR */}
      <section aria-labelledby="pm-referral" className="mb-16 rounded-2xl bg-blue-50 border border-blue-100 p-6">
        <div className="flex items-start gap-4">
          <Gift size={28} className="text-blue-600 mt-1 shrink-0"/>
          <div>
            <h2 id="pm-referral" className="text-2xl font-bold text-slate-900 mb-2">{c.refTitle}</h2>
            <p className="text-slate-700 mb-2">{c.refPara}</p>
            <p className="text-sm text-slate-600">
              {c.refSocioLead}{" "}
              <Link to="/socio/registro" className="text-blue-700 underline">{c.refSocioLink}</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section aria-labelledby="pm-faq" className="mb-16">
        <h2 id="pm-faq" className="text-2xl font-bold text-slate-900 mb-6">{c.faqTitle}</h2>
        <div className="space-y-4">
          {c.faq.map(({q, a}) => (
            <details key={q} className="rounded-xl border border-slate-200 bg-white p-4 group">
              <summary className="font-semibold text-slate-900 cursor-pointer flex items-center justify-between">
                {q}
                <ArrowRight size={16} className="text-slate-400 group-open:rotate-90 transition"/>
              </summary>
              <p className="text-sm text-slate-600 mt-3">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CITY LINKS */}
      {cities.length > 0 && (
        <section aria-labelledby="pm-cities" className="pt-8 border-t border-slate-200">
          <h2 id="pm-cities" className="text-xl font-bold text-slate-900 mb-3">{c.citiesTitle}</h2>
          <p className="text-sm text-slate-600 mb-3">{c.citiesLead}</p>
          <ul className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {cities.map(city => (
              <li key={city}>
                <Link className="text-blue-600 hover:underline" to={`/para-medicos/${slug(city)}`}>
                  {c.cityLink(city)}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* FINAL CTA */}
      <div className="mt-14 flex flex-wrap gap-3">
        <Button data-testid="pm-cta-final" onClick={() => nav("/doctor/registro")} className="bg-blue-600 hover:bg-blue-700 h-11 px-6">
          {c.ctaStart}
        </Button>
      </div>
    </div>
  );
}

// ---------- i18n content for ParaMedicosCity ----------
const PMC_CONTENT = {
  es: {
    breadcrumb: "Para médicos",
    seoTitle: c => `Software para consultorio médico en ${c}`,
    seoDesc: c => `Agendik es el software de agenda para médicos en ${c}. Recordatorios automáticos, agenda 24/7, prueba gratis 30 días. $49/mes, sin permanencia.`,
    h1: c => `Software para consultorio médico en ${c}`,
    p1: c => <>Modernice su consultorio en {c} con Agendik. Sus pacientes de {c} reservan en línea 24/7 y reciben recordatorios automáticos por email y WhatsApp. Usted gestiona toda su agenda desde su teléfono, entre dos consultas.</>,
    p2: c => <>Que su consultorio esté en el centro de {c}, en la periferia o compartido en varias direcciones, Agendik se adapta: horarios flexibles, tarifas configurables, métodos de pago (efectivo, transferencia, DeUna, tarjetas) y seguros aceptados.</>,
    p3: c => <><strong>$49/mes</strong>. <strong>30 días de prueba gratuita</strong>. Sin contrato, cancele cuando quiera. Y con el programa <strong>Socio referidor</strong> obtenga $7 de descuento al mes por cada colega de {c} que se una, hasta gratis.</>,
    bullets: [
      "Agenda en línea 24/7 para sus pacientes",
      "Recordatorios automáticos: menos ausencias",
      "Aplicación móvil (Android e iPhone)",
      "Perfil verificado por Agendik",
      "Datos cifrados, cumplimiento LOPDP",
      "Soporte en español, por WhatsApp",
    ],
    ctaRegister: c => `Registrarme como médico en ${c}`,
    ctaMore: "Ver todas las funciones",
    footer: c => <>¿Busca médicos en {c}? <Link className="text-blue-600 hover:underline" to={`/medicos/${slug(c)}`}>Vea los médicos Agendik en {c}</Link>.</>,
  },
  fr: {
    breadcrumb: "Pour les médecins",
    seoTitle: c => `Logiciel pour cabinet médical à ${c}`,
    seoDesc: c => `Agendik est le logiciel d'agenda pour les médecins à ${c}. Rappels automatiques, agenda 24h/24, 30 jours d'essai gratuit. $49/mois, sans engagement.`,
    h1: c => `Logiciel pour cabinet médical à ${c}`,
    p1: c => <>Modernisez votre cabinet à {c} avec Agendik. Vos patients de {c} réservent en ligne 24h/24 et reçoivent des rappels automatiques par email et WhatsApp. Vous gérez tout votre agenda depuis votre téléphone, entre deux consultations.</>,
    p2: c => <>Que votre cabinet soit au centre de {c}, en périphérie ou partagé sur plusieurs adresses, Agendik s'adapte : horaires flexibles, tarifs paramétrables, moyens de paiement (espèces, virement, DeUna, cartes) et assurances acceptées.</>,
    p3: c => <><strong>$49/mois</strong>. <strong>30 jours d'essai gratuit</strong>. Sans contrat, annulez quand vous voulez. Et avec le programme <strong>Socio referidor</strong>, obtenez $7 de réduction mensuelle pour chaque confrère de {c} qui s'inscrit, jusqu'à la gratuité.</>,
    bullets: [
      "Agenda en ligne 24h/24 pour vos patients",
      "Rappels automatiques : moins d'absences",
      "Application mobile (Android et iPhone)",
      "Profil vérifié par Agendik",
      "Données chiffrées, conformité LOPDP",
      "Support en français et espagnol, par WhatsApp",
    ],
    ctaRegister: c => `M'inscrire comme médecin à ${c}`,
    ctaMore: "Voir toutes les fonctionnalités",
    footer: c => <>Vous cherchez des médecins à {c} ? <Link className="text-blue-600 hover:underline" to={`/medicos/${slug(c)}`}>Voir les médecins Agendik à {c}</Link>.</>,
  },
  en: {
    breadcrumb: "For doctors",
    seoTitle: c => `Medical practice software in ${c}`,
    seoDesc: c => `Agendik is the scheduling software for doctors in ${c}. Automatic reminders, 24/7 schedule, 30-day free trial. $49/month, no lock-in.`,
    h1: c => `Medical practice software in ${c}`,
    p1: c => <>Modernize your practice in {c} with Agendik. Your patients in {c} book online 24/7 and get automatic reminders by email and WhatsApp. You manage your whole schedule from your phone, between appointments.</>,
    p2: c => <>Whether your practice is in central {c}, in the outskirts, or across several addresses, Agendik adapts: flexible hours, configurable fees, payment methods (cash, transfer, DeUna, cards) and accepted insurances.</>,
    p3: c => <><strong>$49/month</strong>. <strong>30-day free trial</strong>. No contract, cancel anytime. And with the <strong>Socio referidor</strong> program, get $7 off per month for each colleague in {c} who joins, up to free.</>,
    bullets: [
      "24/7 online schedule for your patients",
      "Automatic reminders: fewer no-shows",
      "Mobile app (Android and iPhone)",
      "Profile verified by Agendik",
      "Encrypted data, LOPDP compliance",
      "Support in Spanish, via WhatsApp",
    ],
    ctaRegister: c => `Sign up as a doctor in ${c}`,
    ctaMore: "See all features",
    footer: c => <>Looking for doctors in {c}? <Link className="text-blue-600 hover:underline" to={`/medicos/${slug(c)}`}>See Agendik doctors in {c}</Link>.</>,
  },
};

/** Route: /para-medicos/:city — doctor-facing city page. Light SEO version. */
export function ParaMedicosCity() {
  const { city } = useParams();
  const cityName = unslug(city);
  const indexed = useIndexedCities();
  const isIndexed = indexed && indexed.has(city);
  const nav = useNavigate();
  const { lang } = useApp();
  const t = PMC_CONTENT[lang] || PMC_CONTENT.es;
  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-12">
      <Seo title={t.seoTitle(cityName)}
           description={t.seoDesc(cityName)}
           noindex={indexed !== null && !isIndexed}/>

      <nav className="text-xs text-slate-500 mb-4">
        <Link to="/para-medicos" className="hover:underline">{t.breadcrumb}</Link>
        <span className="mx-1">›</span>
        <span>{cityName}</span>
      </nav>

      <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
        {t.h1(cityName)}
      </h1>

      <p className="text-lg text-slate-700 mb-4">{t.p1(cityName)}</p>
      <p className="text-base text-slate-700 mb-4">{t.p2(cityName)}</p>
      <p className="text-base text-slate-700 mb-6">{t.p3(cityName)}</p>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {t.bullets.map(a => (
          <li key={a} className="flex gap-2 text-sm text-slate-700">
            <CheckCircle2 size={16} className="text-blue-600 mt-0.5 shrink-0"/>{a}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-3 mb-8">
        <Button data-testid="pm-city-cta" onClick={() => nav("/doctor/registro")} className="bg-blue-600 hover:bg-blue-700 h-11 px-6">
          {t.ctaRegister(cityName)}
        </Button>
        <Button data-testid="pm-city-cta-more" variant="outline" onClick={() => nav("/para-medicos")} className="h-11 px-6">
          {t.ctaMore}
        </Button>
      </div>

      <p className="text-sm text-slate-500">{t.footer(cityName)}</p>
    </div>
  );
}

// ---------- i18n content for MedicosCity (patient-facing) ----------
const MC_CONTENT = {
  es: {
    breadcrumb: "Médicos",
    specTitle: (s, c) => `${s} en ${c}`,
    cityTitle: c => `Médicos en ${c}`,
    specDesc: (s, c) => `Encuentre un especialista en ${s} en ${c} y reserve su cita en línea con Agendik. Perfiles verificados, agenda 24/7.`,
    cityDesc: c => `Todos los médicos verificados de ${c} disponibles en Agendik. Reserve su cita médica en línea en 2 clics.`,
    loading: "Cargando…",
    emptySpec: (s, c) => `Aún no hay médicos de ${s} disponibles en ${c}.`,
    emptyCity: c => `Aún no hay médicos disponibles en ${c}.`,
    seeAll: "Ver todos los médicos Agendik →",
    isDocQ: c => `¿Es médico en ${c}?`,
    discoverLink: c => `Descubra Agendik para médicos en ${c}`,
  },
  fr: {
    breadcrumb: "Médecins",
    specTitle: (s, c) => `${s} à ${c}`,
    cityTitle: c => `Médecins à ${c}`,
    specDesc: (s, c) => `Trouvez un spécialiste en ${s} à ${c} et réservez votre consultation en ligne avec Agendik. Profils vérifiés, agenda 24h/24.`,
    cityDesc: c => `Tous les médecins vérifiés de ${c} disponibles sur Agendik. Réservez votre consultation en ligne en 2 clics.`,
    loading: "Chargement…",
    emptySpec: (s, c) => `Il n'y a pas encore de médecins en ${s} disponibles à ${c}.`,
    emptyCity: c => `Il n'y a pas encore de médecins disponibles à ${c}.`,
    seeAll: "Voir tous les médecins Agendik →",
    isDocQ: c => `Vous êtes médecin à ${c} ?`,
    discoverLink: c => `Découvrir Agendik pour les médecins à ${c}`,
  },
  en: {
    breadcrumb: "Doctors",
    specTitle: (s, c) => `${s} in ${c}`,
    cityTitle: c => `Doctors in ${c}`,
    specDesc: (s, c) => `Find a specialist in ${s} in ${c} and book your appointment online with Agendik. Verified profiles, 24/7 schedule.`,
    cityDesc: c => `All verified doctors in ${c} available on Agendik. Book your medical appointment online in 2 clicks.`,
    loading: "Loading…",
    emptySpec: (s, c) => `No doctors in ${s} available in ${c} yet.`,
    emptyCity: c => `No doctors available in ${c} yet.`,
    seeAll: "See all Agendik doctors →",
    isDocQ: c => `Are you a doctor in ${c}?`,
    discoverLink: c => `Discover Agendik for doctors in ${c}`,
  },
};

/** Route: /medicos/:city or /medicos/:city/:specialty — patient-facing city page. */
export function MedicosCity() {
  const { city, specialty } = useParams();
  const cityName = unslug(city);
  const specialtyName = specialty ? unslug(specialty) : "";
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang } = useApp();
  const t = MC_CONTENT[lang] || MC_CONTENT.es;
  useEffect(() => {
    const params = { city: cityName };
    if (specialtyName) params.specialty = specialtyName;
    api.get("/doctors", { params })
      .then(r => { setDoctors(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [cityName, specialtyName]);
  const realDoctors = doctors.filter(d => !d.is_demo);
  const title = specialtyName ? t.specTitle(specialtyName, cityName) : t.cityTitle(cityName);
  const desc = specialtyName ? t.specDesc(specialtyName, cityName) : t.cityDesc(cityName);
  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10">
      <Seo title={title} description={desc} noindex={!loading && realDoctors.length === 0}/>

      <nav className="text-xs text-slate-500 mb-4">
        <Link to="/buscar" className="hover:underline">{t.breadcrumb}</Link>
        <span className="mx-1">›</span>
        {specialty ? (
          <>
            <Link to={`/medicos/${city}`} className="hover:underline">{cityName}</Link>
            <span className="mx-1">›</span>
            <span>{specialtyName}</span>
          </>
        ) : (
          <span>{cityName}</span>
        )}
      </nav>

      <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">{title}</h1>
      <p className="text-slate-600 mb-6">{desc}</p>

      {loading ? <p className="text-slate-500">{t.loading}</p>
       : doctors.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <p className="text-slate-700 mb-4">
            {specialtyName ? t.emptySpec(specialtyName, cityName) : t.emptyCity(cityName)}
          </p>
          <Link to="/buscar" className="text-blue-600 hover:underline text-sm">{t.seeAll}</Link>
        </div>
      )
       : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {doctors.map(d => (
            <li key={d.id}>
              <Link to={d.profile_slug ? `/${d.profile_slug}` : `/doctor/${d.id}`}
                    data-testid={`medicos-city-doctor-${d.id}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:border-blue-300 transition">
                {d.photo_url ? <img alt={d.full_name} src={d.photo_url} className="w-14 h-14 rounded-lg object-cover"/>
                             : <div className="w-14 h-14 rounded-lg bg-blue-50 flex items-center justify-center"><Stethoscope size={20} className="text-blue-600"/></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{drName(d)}</div>
                  <div className="text-xs text-blue-600">{d.specialty}</div>
                  <div className="text-xs text-slate-500 truncate"><MapPin size={11} className="inline"/> {d.city}</div>
                </div>
                <ArrowRight size={16} className="text-slate-400"/>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-10 pt-8 border-t border-slate-200 text-sm text-slate-600">
        {t.isDocQ(cityName)}
        {" "}
        <Link className="text-blue-600 hover:underline" to={`/para-medicos/${city}`}>
          {t.discoverLink(cityName)}
        </Link>.
      </div>
    </div>
  );
}
